import { Tooltip } from '@nextui-org/react'
import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useUTMParams } from '@/hooks/useUTMParams'


import { useProviderLoader } from '@/Providers/useProviderLoader'
import { ConfettiAnimation } from '@/components/custom-components'
import { Button, Card, CardBody, CardHeader, Image } from '@/components/nextui'
import { useAuthApi } from '@/services/api/authApi'
import { ErrorHandlingStrategy, MessageLevel } from '@/types/api'

export const ConfirmEmail = (): JSX.Element => {
    const [searchParams] = useSearchParams()
    const token = searchParams.get('token')
    const navigate = useNavigate()
    const { navigateWithUTM } = useUTMParams()
    const authApi = useAuthApi({
        errorHandling: ErrorHandlingStrategy.RETURN,
        messageLevel: MessageLevel.ERRORS_AND_INFO_ONLY
    })
    const { t } = useTranslation()
    const [showConfetti, setShowConfetti] = useState(false)
    const [showButtonNavigate, setShowButtonNavigate] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const { showLoader, hideLoader } = useProviderLoader()

    useEffect(() => {
        showLoader()
        const confirmEmail = async () => {
            if (!token) {
                console.error('No token provided')

                return
            }
            try {
                const response = await authApi.confirmEmail(token)

                if (response.status === 200) {
                    setShowConfetti(true)
                    setShowButtonNavigate(true)
                    setErrorMessage('')
                } else {
                    setErrorMessage(
                        response.message?.[0]?.messageDesc ||
                            'error intento del sistema, comuniquese por favor con la universidad'
                    )
                    setShowButtonNavigate(false)
                }
            } catch (error) {
                console.error('Error confirming email:', error)
                setShowConfetti(false)
            } finally {
                hideLoader()
            }
        }

        confirmEmail()
    }, [])

    return (
        <article className="flex flex-col justify-center items-center p-4 min-h-screen bg-gradient-ean-2 dark:bg-gradient-ean">
            <ConfettiAnimation isVisible={showConfetti} />
            <motion.div
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 2, ease: 'easeOut' }}
                className="w-full max-w-md"
            >
                <Card className="w-full backdrop-blur-md bg-white/90 dark:bg-gray-800/90">
                    <CardHeader className="flex flex-col gap-4 justify-center items-center">
                        <Image
                            src="/LogoEAN.jpg"
                            alt="logo"
                            className="w-[150px]"
                        />
                    </CardHeader>
                    <CardBody className="flex flex-col items-center gap-6 p-6">
                        <h1 className="text-2xl font-bold text-center">
                            {showButtonNavigate
                                ? t('emailConfirmation.title')
                                : t('emailConfirmation.titleError')}
                        </h1>
                        <p className="text-center text-default-600">
                            {showButtonNavigate
                                ? t('emailConfirmation.description')
                                : t('emailConfirmation.descriptionError', {
                                      errorMessage
                                  })}
                        </p>
                        <Tooltip
                            content={errorMessage}
                            color="default"
                            delay={0}
                            placement="right"
                            closeDelay={0}
                        >
                            <div className="w-full">
                                {' '}
                                <Button
                                    color="primary"
                                    className="w-full"
                                    onPress={() => navigateWithUTM('/login')}
                                    isDisabled={!showButtonNavigate}
                                >
                                    {t('emailConfirmation.goToLogin')}
                                </Button>
                            </div>
                        </Tooltip>
                    </CardBody>
                </Card>
            </motion.div>
        </article>
    )
}
